import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Exercise, ExerciseResult } from "../schemas/workout";

interface EditExerciseModalProps {
  open: boolean;
  exercise: Exercise;
  result?: any; // Optionally pass the result for editing
  onClose: () => void;
  onSave: (exercise: Exercise, result?: ExerciseResult) => void;
}

export function EditExerciseModal({ open, exercise, result, onClose, onSave }: EditExerciseModalProps) {
  const [name, setName] = useState(exercise.name || "");
  const [type, setType] = useState(exercise.type || "strength");
  const [restTime, setRestTime] = useState(exercise.rest_time || 0);
  const [notes, setNotes] = useState(exercise.notes || "");
  // Structure editing
  const [structure, setStructure] = useState<any>(exercise.structure);

  // Result fields
  const [completed, setCompleted] = useState(result?.completed ?? false);
  const [setsCompleted, setSetsCompleted] = useState(result?.sets_completed ?? 0);
  const [actualSets, setActualSets] = useState(result?.actual_sets ?? "");
  const [actualReps, setActualReps] = useState(result?.actual_reps ?? "");
  const [actualDuration, setActualDuration] = useState(result?.actual_duration ?? "");
  const [resultNotes, setResultNotes] = useState(result?.notes ?? "");

  // Reset modal state when exercise or result changes
  useEffect(() => {
    setName(exercise.name || "");
    setType(exercise.type || "strength");
    setRestTime(exercise.rest_time || 0);
    setNotes(exercise.notes || "");
    setStructure(exercise.structure);
    setCompleted(result?.completed ?? false);
    setSetsCompleted(result?.sets_completed ?? 0);
    setActualSets(result?.actual_sets ?? "");
    setActualReps(result?.actual_reps ?? "");
    setActualDuration(result?.actual_duration ?? "");
    setResultNotes(result?.notes ?? "");
  }, [exercise, result, open]);

  // Handle structure changes
  const handleStructureChange = (field: string, value: any) => {
    setStructure((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    const updated: Exercise = {
      ...exercise,
      name,
      type,
      rest_time: Number(restTime),
      notes,
      structure,
    };
    const updatedResult: ExerciseResult = {
      ...updated,
      completed,
      sets_completed: Number(setsCompleted),
      actual_sets: actualSets !== "" ? Number(actualSets) : undefined,
      actual_reps: actualReps !== "" ? Number(actualReps) : undefined,
      actual_duration: actualDuration !== "" ? Number(actualDuration) : undefined,
      notes: resultNotes,
    };
    onSave(updated, updatedResult);
  };

  // Render structure fields based on type
  let structureFields = null;
  if (structure?.type === "sets") {
    structureFields = (
      <>
        <div>
          <label className="block text-sm font-medium mb-1">Sets</label>
          <Input
            type="number"
            min="1"
            value={structure.sets}
            onChange={e => handleStructureChange("sets", Number(e.target.value))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Reps</label>
          <Input
            type="number"
            min="1"
            value={structure.reps}
            onChange={e => handleStructureChange("reps", Number(e.target.value))}
          />
        </div>
      </>
    );
  } else if (structure?.type === "timed") {
    structureFields = (
      <>
        <div>
          <label className="block text-sm font-medium mb-1">Sets</label>
          <Input
            type="number"
            min="1"
            value={structure.sets}
            onChange={e => handleStructureChange("sets", Number(e.target.value))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Duration (seconds)</label>
          <Input
            type="number"
            min="1"
            value={structure.duration}
            onChange={e => handleStructureChange("duration", Number(e.target.value))}
          />
        </div>
      </>
    );
  } else if (structure?.type === "cadence") {
    structureFields = (
      <>
        <div>
          <label className="block text-sm font-medium mb-1">Sets</label>
          <Input
            type="number"
            min="1"
            value={structure.sets}
            onChange={e => handleStructureChange("sets", Number(e.target.value))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Reps</label>
          <Input
            type="number"
            min="1"
            value={structure.reps}
            onChange={e => handleStructureChange("reps", Number(e.target.value))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Tempo</label>
          <Input
            value={structure.tempo}
            onChange={e => handleStructureChange("tempo", e.target.value)}
          />
        </div>
      </>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Exercise</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Exercise name" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Type</label>
            <select
              className="border rounded px-2 py-1 w-full bg-white dark:bg-zinc-800 text-black dark:text-white"
              value={type}
              onChange={e => setType(e.target.value as Exercise["type"])}
            >
              <option value="strength">Strength</option>
              <option value="cardio">Cardio</option>
              <option value="flexibility">Flexibility</option>
              <option value="hiit">HIIT</option>
              <option value="other">Other</option>
              <option value="sets">Sets</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Rest Time (seconds)</label>
            <Input
              type="number"
              min="0"
              value={restTime}
              onChange={e => setRestTime(Number(e.target.value))}
              placeholder="e.g. 60"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Notes (optional)</label>
            <Textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Anything else about this exercise..."
              rows={2}
            />
          </div>
          {/* Structure fields */}
          {structureFields && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{structureFields}</div>
          )}
          {/* Result fields */}
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center mb-2">
              <Checkbox checked={completed} onCheckedChange={setCompleted} id="completed-checkbox" />
              <label htmlFor="completed-checkbox" className="ml-2 text-sm font-medium">Completed</label>
            </div>
            {/* Only show result fields if exercise is marked completed */}
            {completed && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-1">Sets Completed</label>
                  <Input
                    type="number"
                    min="0"
                    value={setsCompleted}
                    onChange={e => setSetsCompleted(Number(e.target.value))}
                  />
                </div>
                {/* Only show actuals for relevant types */}
                {structure?.type === "sets" || structure?.type === "cadence" ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1">Actual Sets (optional)</label>
                      <Input
                        type="number"
                        min="0"
                        value={actualSets}
                        onChange={e => setActualSets(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Actual Reps (optional)</label>
                      <Input
                        type="number"
                        min="0"
                        value={actualReps}
                        onChange={e => setActualReps(e.target.value)}
                      />
                    </div>
                  </>
                ) : null}
                {structure?.type === "timed" ? (
                  <div>
                    <label className="block text-sm font-medium mb-1">Actual Duration (seconds, optional)</label>
                    <Input
                      type="number"
                      min="0"
                      value={actualDuration}
                      onChange={e => setActualDuration(e.target.value)}
                    />
                  </div>
                ) : null}
                <div>
                  <label className="block text-sm font-medium mb-1">Result Notes (optional)</label>
                  <Textarea
                    value={resultNotes}
                    onChange={e => setResultNotes(e.target.value)}
                    rows={2}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
